import axiosInstance from "./axiosInstance"
import { UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, useRef } from "react";

// Define the endpoint path
export const PATHS = Object.freeze({
  GET_USER_INFO: "user/hierarchy",
  GET_FEATURE_FLAGS :"/feature-flag",
  GET_CONTACTS : "/contacts",
  GET_ACCOUNTS: "/accounts-orgs",
  GET_USER_PROFILE: "/user-profiles",
  GET_USER_NOTIFICATION: "/task/user-notifications",
  GET_USER_NOTIFICATION_COUNT : "/unread-notifications-count",
  POST_USER_NOTIFICATION_READ: "/task/user-notifications/read",
  GET_NEW_FEATURE_FLAGS: "/feature-flags",
  POST_LOGOUT: "auth/logout",
  POST_ORG_SUMMARY: "/org-summary",
  GET_EMAIL_SUMMARY: "/email-details",
  GET_ORG_SUMMARY_FILTERS: "/org-summary-filters",
  GET_IMPERSONATE_PROFILES: "/allowed-impersonate-users",
  GET_USER_GROUPS: "/user-groups",
  GET_ORG_SUMMARY_EXPORT: "/org-summary/export",
  GET_EMAIL_DATA_EXPORT: "/email-data-export",
  GET_FINGERTIP_TREND_LINE_INTERACTIONS: "/fingertip-trend-line",
  GET_ORG_SUMMARY_SPACES: "/org-summary-space-mode",
});       

interface GetHierarchyParams {
  employeePorId: string;
  depthLevel?: number;
}

interface TranscriptEvent {
  speaker: string;
  transcript: string;
}

interface UseLiveTranscriptParams {
  interaction_id: string;
  enabled: boolean;
}
interface ContactsParams {
  offset: number;
  limit: number;
}

interface AccountsParams {
  offset: number;
  limit: number;
  startTime: string;
  endTime: string;
  relationshipType: string;
  employeePorId: string | undefined;
  sortBy: string;
  sortOrder: "asc" | "desc";
  accountName?: string;
}

interface userProfileParams {
  profileOrgId: string;
  employeeEmail: string;
}

interface UserNotificationParams {
  userProfileOrgId: string;
}

interface OrgSummaryParams {
  limit: number;
  offset: number;
  sortOrder: "asc" | "desc";
  startTime: string;
  endTime: string;
  orgName?: string;
  overallRating?: string[];
  mrrOperator?: "gt" | "lt";
  mrrValue?: number;
  sortBy: string;
  employeePorId: string;
  clientTypes?: string[];
  accountManagerName?: string;
  advertisingAccountManagerName?: string;
  timeZone? : string;
  isAccountActive?: boolean;
  relationshipType?: string;
  trajectory?: string;
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
  isSpaceMode?: boolean;
  spaceIds?: string[];
  isInteractionPresent?: boolean;
  isFlightRisk?: boolean;
  accountViewMode?: "AccountsTimeLineView" | "AccountsSummaryView";
}

interface EmailSummaryParams {
  emailMessageIds: string[];
}

interface ImpersonateProfileParams {
  accountId: string;
  employeePorId: string;
}

interface UserGroupParams {
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
}

interface EmailDataExportParams {
  startTime: string;
  endTime: string;
}
interface FingertipTrendLineInteractionParams {
  subOrgId: string;
  startTime: string;
  endTime: string;
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
}

const getHierarchyInfo = async (params: GetHierarchyParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_INFO, {
    params: {
      employeePorId: params.employeePorId,
      depthLevel: params.depthLevel,
    },
  });
  return response.data;
};

// React Query hook
const useGetHierarchyInfo = (params: GetHierarchyParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_user_info", stringifiedParams],
    queryFn: () => getHierarchyInfo(params),
    enabled: enabled,
    retry: 0,
  });
};

const baseUrl = import.meta.env.VITE_JAVA_SERVICE_ACCOUNTS_BASE;

const validateEmail = async (email: string): Promise<any> => {
  const apiUrl = `${baseUrl}validateEmail?employeeEmail=${email}`;

  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to validate email. Status: ${response.status}`);
  }

  const data = await response.json();
  return data;
};

const useValidateEmail = (
  email: string,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["validate_email", email],
    queryFn: () => validateEmail(email),
    enabled,
    retry: 0,
  });
};

const getAccountDetails = async (authId: string): Promise<any> => {
  const apiUrl = `${baseUrl}${authId}/account-details`;

  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const error: any = new Error(`Failed to fetch account details`);
    error.status = response.status;
    error.data = errorBody;
    throw error;
  }

  const data = await response.json();
  return data;
};

const useGetAccountDetails = (
  authId: string,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_account_details", authId],
    queryFn: () => getAccountDetails(authId),
    enabled,
    retry: 0,
  });
};

export const useLiveTranscript = ({
  interaction_id,
  enabled,
}: UseLiveTranscriptParams) => {
  const [transcripts, setTranscripts] = useState<TranscriptEvent[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!interaction_id || !enabled) return;

    const streamUrl = `${baseUrl}transcript-stream?interaction_id=${interaction_id}`;
    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener("transcript", (event: MessageEvent) => {
      try {
        const data: TranscriptEvent = JSON.parse(event.data);
        setTranscripts((prev) => [...prev, data]);
      } catch (err) {
        console.error("Failed to parse transcript:", err);
      }
    });

    eventSource.onerror = (err) => {
      console.error("SSE connection error:", err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [interaction_id, enabled]);

  return { transcripts };
};

const getFeatureFlags = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_FEATURE_FLAGS);
  return response;
};

const useGetFeatureFlags = (enabled: boolean): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_feature_flags"],
    queryFn: getFeatureFlags,
    enabled,
    retry: 0,
  });
};

const getContacts = async (params: ContactsParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_CONTACTS,
    {
      params: {
        limit: params.limit,
        offset: params.offset,
      }
    }
  );
  return response.data;
}

const useGetContacts = (
  params: ContactsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_contacts", params.offset, params.limit],
    queryFn: () => getContacts(params),
    enabled,
    retry: 0,     
  });
}

const getAccounts = async (params: AccountsParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACCOUNTS,{
    params: {
      offset: params.offset,
      limit: params.limit,
      relationshipType: params.relationshipType,
      startTime: params.startTime,
      endTime: params.endTime,
      employeePorId: params.employeePorId,
      sortOrder: params.sortOrder,
      sortBy: params.sortBy,
      ...(params.accountName && { accountName: params.accountName }),
    }
  });
  return response.data;
}

const useGetAccounts = (
  params: AccountsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_accounts", stringifiedParams],
    queryFn: () => getAccounts(params),
    enabled,
    retry: 0,
  });
}

const getUserProfile = async (params: userProfileParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_PROFILE, {
    params: {
      profileOrgId: params.profileOrgId,
      employeeEmail: params.employeeEmail,
    }
  });
  return response.data;
}

const useGetUserProfile = (
  params: userProfileParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_user_profiles",stringifiedParams ],
    queryFn: () => getUserProfile(params),
    enabled,
    retry: 0,
  });
};

const getUserNotifications = async (params: UserNotificationParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_NOTIFICATION, {
    params: {
      userProfileOrgId: params.userProfileOrgId
    }
  });

  return response;
};

const useGetUserNotifications = (
  params: UserNotificationParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery({
    queryKey: ["get_user_notifications", stringifiedParams],
    queryFn: () => getUserNotifications(params),
    enabled,
    retry: 0,
  });
};

const getUserNotificationCount = async(params: UserNotificationParams) :Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_NOTIFICATION_COUNT, {
    params: {
      userProfileOrgId: params.userProfileOrgId
    }
  });

  return response;
}

const useGetNotificationCount = (
  params: UserNotificationParams,
  enabled: boolean,
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery({
    queryKey: ["get_user_notification_count", stringifiedParams],
    queryFn: () => getUserNotificationCount(params),
    enabled,
    retry: 0,
    refetchInterval: 60 * 60 * 1000, // 1 hour
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
};

const postMarkNotificationRead = async (params: {
  userProfileOrgId: string;
  notificationIds: string[];
}): Promise<any> => {

  return axiosInstance.post(
    PATHS.POST_USER_NOTIFICATION_READ,
    {
      notificationIds: params.notificationIds, // array → repeated params
    },
    {
      params: {
        userProfileOrgId: params.userProfileOrgId,
      },
    }
  );
};

const usePostNotificationRead = () => {
  return useMutation({
    mutationFn: postMarkNotificationRead,
    onError: (err) => {
      console.error("Failed to mark notification as read:", err);
    },
  });
};

const getNewFeatureFlags = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_NEW_FEATURE_FLAGS);
  return response;
};

const useGetNewFeatureFlags = (enabled: boolean): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_new_feature_flags"],
    queryFn: getNewFeatureFlags,
    enabled,
    retry: (count, error: any) => {
      if (error?.response?.status === 403) return false;
      return count < 5;
    },
    retryDelay: 1000,
  });
};

const logoutUser = async (): Promise<void> => {
  const apiUrl = `${baseUrl}${PATHS.POST_LOGOUT}`; // VITE_JAVA_SERVICE_ACCOUNTS_BASE

  await fetch(apiUrl, {
    method: "POST",
    credentials: "include", // send cookies
  });
};

const getOrgSummary = async (params: OrgSummaryParams): Promise<any> => {
  const response = await axiosInstance.post(PATHS.POST_ORG_SUMMARY, 
    {
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.orgName && { orgName: params.orgName }),
      ...(params.overallRating && { overallRating: params.overallRating }),
      ...(params.mrrOperator && { mrrOperator: params.mrrOperator }),
      ...(params.mrrValue !== undefined && { mrrValue: params.mrrValue }),
      ...(params.employeePorId && { employeePorId: params.employeePorId}),
      ...(params.clientTypes && { clientTypes: params.clientTypes }),
      ...(params.accountManagerName && { accountManagerName: params.accountManagerName }),
      ...(params.advertisingAccountManagerName && { advertisingAccountManagerName: params.advertisingAccountManagerName }),
      ...(params.timeZone && { timeZone: params.timeZone }),
      ...(params.isAccountActive !== undefined && { isAccountActive: params.isAccountActive }),
      ...(params.relationshipType && { relationshipType: params.relationshipType }),
      ...(params.trajectory && { trajectory: params.trajectory }),
      ...(params.isSpaceMode && { isSpaceMode: params.isSpaceMode }),
      ...(params.spaceIds && { spaceIds: params.spaceIds }),
       ...(params.isInteractionPresent && { isInteractionPresent: params.isInteractionPresent }),
       isFlightRisk: params.isFlightRisk,
       ...(params.accountViewMode && { accountViewMode: params.accountViewMode }),
    },
    {
    params: {
      limit: params.limit,
      offset: params.offset,
      sortOrder: params.sortOrder,
      sortBy: params.sortBy,
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    }
  });
  return response.data;
};

const useGetOrgSummary = (
  params: OrgSummaryParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_org_summary", stringifiedParams],
    queryFn: () => getOrgSummary(params),
    enabled,
    retry: 0,
  });
};

const getOrgSummarySpaceMode = async (params: OrgSummaryParams): Promise<any> => {
  const response = await axiosInstance.post(PATHS.GET_ORG_SUMMARY_SPACES, 
    {
      startTime: params.startTime,
      endTime: params.endTime,
      employeePorId: params.employeePorId,
      ...(params.orgName && { orgName: params.orgName }),
      ...(params.relationshipType && { relationshipType: params.relationshipType }),
      ...(params.spaceIds && { spaceIds: params.spaceIds }),
    },
    {
      params: {
        limit: params.limit,
        offset: params.offset,
        sortOrder: params.sortOrder,
        sortBy: params.sortBy,
      }
    }
  );
  return response.data;
};

const useGetOrgSummarySpaceMode = (
  params: OrgSummaryParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_org_summary_space_mode", stringifiedParams],
    queryFn: () => getOrgSummarySpaceMode(params),
    enabled,
    retry: 0,
  });
};

const getEmailSummary = async (params: EmailSummaryParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_EMAIL_SUMMARY, {
    params: {
      emailMessageIds: params.emailMessageIds.join(","),
    },
  });

  return response.data;
};

const useGetEmailSummary = (
  params: EmailSummaryParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_email_summary", stringifiedParams],
    queryFn: () => getEmailSummary(params),
    enabled,
    retry: 0,
  });
};

const getOrgSummaryFilters = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ORG_SUMMARY_FILTERS);
  return response.data;
};

const useGetOrgSummaryFilters = (enabled: boolean): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_org_summary_filters"],
    queryFn: getOrgSummaryFilters,
    enabled,
    retry: 0,
  });
};

const getImpersonateProfile = async (params: ImpersonateProfileParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_IMPERSONATE_PROFILES, {
    params: {
      accountId: params.accountId,
      employeePorId: params.employeePorId
    }
  });
  return response;
};

const useGetImpersonateProfile = (
  params: ImpersonateProfileParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_impersonate_profile", stringifiedParams],
    queryFn: ()=> getImpersonateProfile(params),
    enabled,
    retry: 0,
  })
}
const getUserGroups = async (params: UserGroupParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_GROUPS, {
    params: {
      impersonatingPorId: params.impersonatingPorId,
      accessLevel: params.accessLevel,
    },
  });
  return response.data;
};

const useGetUserGroups = (params: UserGroupParams, enabled: boolean): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_user_groups", stringifiedParams],
    queryFn: ()=> getUserGroups(params),
    enabled,
    retry: 0,
  });
};

const getOrgSummaryExport = async (params: OrgSummaryParams): Promise<Blob> => {
  const response = await axiosInstance.post(PATHS.GET_ORG_SUMMARY_EXPORT, 
    {
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.orgName && { orgName: params.orgName }),
      ...(params.overallRating && { overallRating: params.overallRating }),
      ...(params.mrrOperator && { mrrOperator: params.mrrOperator }),
      ...(params.mrrValue !== undefined && { mrrValue: params.mrrValue }),
      ...(params.employeePorId && { employeePorId: params.employeePorId}),
      ...(params.clientTypes && { clientTypes: params.clientTypes }),
      ...(params.accountManagerName && { accountManagerName: params.accountManagerName }),
      ...(params.advertisingAccountManagerName && { advertisingAccountManagerName: params.advertisingAccountManagerName }),
      ...(params.timeZone && { timeZone: params.timeZone }),
      ...(params.isAccountActive !== undefined && { isAccountActive: params.isAccountActive }),
      ...(params.relationshipType && { relationshipType: params.relationshipType }),
      ...(params.trajectory && { trajectory: params.trajectory }),
      ...(params.isSpaceMode && { isSpaceMode: params.isSpaceMode }),
      ...(params.spaceIds && { spaceIds: params.spaceIds }),
    },
    {
    params: {
      limit: params.limit,
      offset: params.offset,
      sortOrder: params.sortOrder,
      sortBy: params.sortBy,
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    },
    responseType: "blob",
  });
  return response.data;
};

const useExportOrgSummary = (
  handlerFunctions: any = {}
): ReturnType<typeof useMutation<Blob, any, OrgSummaryParams>> => {
  return useMutation({
    mutationKey: ["export_org_summary"],
    mutationFn: (params: OrgSummaryParams) => getOrgSummaryExport(params),
    ...handlerFunctions,
  });
};

const getEmailDataExport = async (params: EmailDataExportParams): Promise<Blob> => {
  const response = await axiosInstance.post(
    PATHS.GET_EMAIL_DATA_EXPORT,
    {},
    {
      params: {
        startTime: params.startTime,
        endTime: params.endTime,
      },
      responseType: "blob",
    }
  );
  return response.data;
};

const getFingertipTrendLineInteractions = async (params: FingertipTrendLineInteractionParams): Promise<any> => {
  const response = await axiosInstance.post(PATHS.GET_FINGERTIP_TREND_LINE_INTERACTIONS, 
    {
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    },
    {
      params: {
        subOrgId: params.subOrgId,
      }
    }
  );
  return response.data;
};

const useGetEmailDataExport = (
  handlerFunctions: any = {}
): ReturnType<typeof useMutation<Blob, any, EmailDataExportParams>> => {
  return useMutation({
    mutationKey: ["export_eemail_data"],
    mutationFn: (params: EmailDataExportParams) => getEmailDataExport(params),
    ...handlerFunctions,
  })
};

const useGetFingertipTrendLineInteractions = (
  params: FingertipTrendLineInteractionParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery({
    queryKey: ["get_fingertip_trend_line_interactions", stringifiedParams],
    queryFn: () => getFingertipTrendLineInteractions(params),
    enabled,
    retry: 0,
  });
};

// Export the API functions
export interface CommonService {
  readonly useGetHierarchyInfo: (
    params: GetHierarchyParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useValidateEmail: (
    email: string,
    enabled: boolean
  ) => UseQueryResult<any, any>;

    readonly useGetAccountDetails: (
      authId: string,
      enabled: boolean
    ) => UseQueryResult<any, any>;
    
    readonly useLiveTranscript: (
      params: UseLiveTranscriptParams
    ) => { transcripts: TranscriptEvent[] };

    readonly useGetFeatureFlags: (
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useGetContacts: (
      params: ContactsParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useGetAccounts: (
      params: AccountsParams,
      enabled: boolean,
    ) => UseQueryResult<any, any>;

    readonly useGetUserProfile: (
      params: userProfileParams,
      enabled: boolean,
    ) => UseQueryResult<any, any>;

    readonly useGetUserNotifications: (
      params: UserNotificationParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;
    
    readonly useGetNotificationCount: (
      params: UserNotificationParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly usePostNotificationRead: (
    ) => ReturnType<typeof usePostNotificationRead>;

    readonly useGetNewFeatureFlags: (
      enabled: boolean
    ) => UseQueryResult<any, any>;    
    readonly logoutUser: () => Promise<any>;

    readonly useGetOrgSummary: (
      params: OrgSummaryParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useGetOrgSummarySpaceMode: (
      params: OrgSummaryParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useGetEmailSummary: (
      params: EmailSummaryParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useGetOrgSummaryFilters: ( 
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useGetImpersonateProfile: (
      params: ImpersonateProfileParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;
    
    readonly useGetUserGroups: (
      params: UserGroupParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;

    readonly useExportOrgSummary: (
      handlerFunctions?: any,
    ) => ReturnType<typeof useMutation<Blob, any, OrgSummaryParams>>;

    readonly useGetEmailDataExport: (
      handlerFunctions?: any,
    ) => ReturnType<typeof useMutation<Blob, any, EmailDataExportParams>>;
    
    readonly useGetFingertipTrendLineInteractions: (
      params: FingertipTrendLineInteractionParams,
      enabled: boolean
    ) => UseQueryResult<any, any>;
}

const CommonService: CommonService = Object.freeze({
    useGetHierarchyInfo,
    useValidateEmail,
    useGetAccountDetails,
    useLiveTranscript,
    useGetFeatureFlags,
    useGetContacts,
    useGetAccounts,
    useGetUserProfile,
    useGetUserNotifications,
    useGetNotificationCount,
    usePostNotificationRead,
    useGetNewFeatureFlags,
    logoutUser,
    useGetOrgSummary,
    useGetOrgSummarySpaceMode,
    useGetEmailSummary,
    useGetOrgSummaryFilters,
    useGetImpersonateProfile,
    useGetUserGroups,
    useExportOrgSummary,
    useGetEmailDataExport,
    useGetFingertipTrendLineInteractions,
});

export default CommonService;
